import { AnyFinancialEntity } from '../../financialEntities';
import { SnapshotBase, VSGContext } from '../types';
import { makeBrokerageAccountValueSeriesGenerator } from './brokerageAccount';
import { makeCarValueSeriesGenerator } from './car';
import { makeCarLoanValueSeriesGenerator } from './carLoan';
import { makeCashHoldingValueSeriesGenerator } from './cashHolding';
import { makeCDValueSeriesGenerator } from './cd';
import { makeCheckingAccountValueSeriesGenerator } from './checkingAccount';
import { makeCreditCardValueSeriesGenerator } from './creditCard';
import { defaultValueNodeParams } from './helpers';
import { makeHomeMortgageValueSeriesGenerator } from './homeMortgage';
import { makeHSAValueSeriesGenerator } from './hsa';
import { makeLineOfCreditValueSeriesGenerator } from './lineOfCredit';
import { makeMoneyMarketAccountValueSeriesGenerator } from './moneyMarketAccount';
import { makeOtherAssetValueSeriesGenerator } from './otherAsset';
import { makeOtherInvestmentValueSeriesGenerator } from './otherInvestment';
import { makeOtherLiabilityValueSeriesGenerator } from './otherLiability';
import { makePlentyGoalAccountValueSeriesGenerator } from './plentyGoalAccount';
import { makeRealEstatePropertyValueSeriesGenerator } from './realEstateProperty';
import { makeRetirementAccountValueSeriesGenerator } from './retirementAccount';
import { makeS529AccountValueSeriesGenerator } from './s529Account';
import { makeSavingsAccountValueSeriesGenerator } from './savingsAccount';
import { makeStockPlanValueSeriesGenerator } from './stockPlan';
import { makeStudentLoanValueSeriesGenerator } from './studentLoan';

export const getFEValueSeriesGenerator = (
    fe: AnyFinancialEntity,
    snapshots: SnapshotBase[],
    ctxParams: Omit<VSGContext, 'baseValueNodeParams'>,
    params?: Record<string, unknown>
) => {
    const ctx = {
        baseValueNodeParams: defaultValueNodeParams(fe),
        ...ctxParams,
    };

    switch (fe.type) {
        case 'BrokerageAccount':
            return makeBrokerageAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'Car':
            return makeCarValueSeriesGenerator(fe, snapshots, ctx, params);
        case 'CarLoan':
            return makeCarLoanValueSeriesGenerator(fe, snapshots, ctx, params);
        case 'CashHolding':
            return makeCashHoldingValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'CD':
            return makeCDValueSeriesGenerator(fe, snapshots, ctx, params);
        case 'CheckingAccount':
            return makeCheckingAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'CreditCard':
            return makeCreditCardValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'HomeMortgage':
            return makeHomeMortgageValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'HSA':
            return makeHSAValueSeriesGenerator(fe, snapshots, ctx, params);
        case 'LineOfCredit':
            return makeLineOfCreditValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'MoneyMarketAccount':
            return makeMoneyMarketAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'OtherAsset':
            return makeOtherAssetValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'OtherInvestment':
            return makeOtherInvestmentValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'OtherLiability':
            return makeOtherLiabilityValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'PlentyGoalAccount':
            return makePlentyGoalAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'RealEstateProperty':
            return makeRealEstatePropertyValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'RetirementAccount':
            return makeRetirementAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'S529Account':
            return makeS529AccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'SavingsAccount':
            return makeSavingsAccountValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'StockPlan':
            return makeStockPlanValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        case 'StudentLoan':
            return makeStudentLoanValueSeriesGenerator(
                fe,
                snapshots,
                ctx,
                params
            );
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = fe;
            throw new Error('Unknown typename in getFEValueSeriesGenerator');
        }
    }
};
